import React, { useEffect, useRef, useState } from 'react';
import { adminStripeAPI } from '../../services/api';

const BUTTON_CLASS =
  'px-4 py-2 rounded text-sm font-bold bg-brand-yellow/15 text-brand-yellow border border-brand-yellow/35 hover:bg-brand-yellow/25 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap';

const defaultRun = () => adminStripeAPI.syncProducts();

const defaultRenderResult = ({ data }) =>
  `Queued ${data?.queued ?? 0}, already synced ${data?.already_synced ?? 0}. Badges update after the jobs run; refresh to see them.`;

/**
 * Admin trigger for a background Stripe job. Defaults are the "Sync Stripe products"
 * backfill (POST /admin/stripe/sync_products), which only reports how many were queued.
 *
 * Generalised so other admin syncs reuse the same markup:
 *   label, pendingLabel, errorText, testIdPrefix  - copy and test ids
 *   run()                                          - starts the job, resolves to an axios response
 *   renderResult({ data, latest, done, gaveUp })   - status text for the current state
 *   poll { request, isDone(data, latest), intervalMs, maxAttempts }
 *        optional: after run() succeeds, call request() every intervalMs (at most
 *        maxAttempts times) until isDone is true. Timers are cleared on unmount.
 */
const StripeSyncButton = ({
  label = 'Sync Stripe products',
  pendingLabel = 'Syncing...',
  errorText = 'Stripe sync failed.',
  testIdPrefix = 'stripe-sync',
  run = defaultRun,
  renderResult = defaultRenderResult,
  poll = null,
  className = 'flex flex-wrap items-center gap-3 mb-4',
}) => {
  const [pending, setPending] = useState(false);
  const [polling, setPolling] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(false);
  const timer = useRef(null);
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
      if (timer.current) clearTimeout(timer.current);
      timer.current = null;
    };
  }, []);

  const startPolling = (data) => {
    const { request, isDone, intervalMs = 3000, maxAttempts = 20 } = poll;
    let attempts = 0;
    setPolling(true);

    const step = async () => {
      timer.current = null;
      attempts += 1;
      let latest = null;
      try {
        const response = await request();
        latest = response?.data || null;
      } catch (e) {
        latest = null; // transient failure: counts as an attempt, keep waiting
      }
      if (!alive.current) return;
      if (latest && isDone(data, latest)) {
        setResult({ data, latest, done: true, gaveUp: false });
        setPolling(false);
        return;
      }
      if (attempts >= maxAttempts) {
        setResult({ data, latest, done: false, gaveUp: true });
        setPolling(false);
        return;
      }
      timer.current = setTimeout(step, intervalMs);
    };

    timer.current = setTimeout(step, intervalMs);
  };

  const handleClick = async () => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
    setPending(true);
    setPolling(false);
    setResult(null);
    setError(false);
    let data;
    try {
      const response = await run();
      data = response?.data || {};
    } catch (e) {
      if (alive.current) {
        setError(true);
        setPending(false);
      }
      return;
    }
    if (!alive.current) return;
    setResult({ data, latest: null, done: !poll, gaveUp: false });
    setPending(false);
    if (poll) startPolling(data);
  };

  const busy = pending || polling;

  return (
    <div className={className}>
      <button
        type="button"
        onClick={handleClick}
        disabled={busy}
        aria-busy={busy || undefined}
        data-testid={`${testIdPrefix}-btn`}
        className={BUTTON_CLASS}
      >
        {busy ? pendingLabel : label}
      </button>
      {result && (
        <span className="text-sm text-gray-300" data-testid={`${testIdPrefix}-result`} role="status">
          {renderResult(result)}
        </span>
      )}
      {error && (
        <span className="text-sm text-red-400" data-testid={`${testIdPrefix}-error`} role="alert">
          {errorText}
        </span>
      )}
    </div>
  );
};

export default StripeSyncButton;
